import { ThemeProvider } from '@fluentui/react';
import { DarkTheme } from '@fluentui/theme-samples';
import { IRadialMenuItem, RadialMenu } from 'components/controls/RadialMenu';
import { IEntitiesContainerVM, IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { Delay, ScrollToPositionCascaded } from 'global/Utility/TSFunctions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    QUICK_NAV_FLOATING_BACKDROP_HIDDEN,
    QUICK_NAV_FLOATING_BACKDROP_VISIBLE
} from '../Pharmacy/PharmacyPlan/PharmacyPlan.constants';
import { IMarkerSet } from './Compare.constants';
import { ICompareConfiguration } from '../CompareLookupDialog/CompareLookupDialog';

interface ICompareRadialNavOverlayProps {
    model: IGroupConfigurationComparisonVM | null;
    isHidden: boolean;
    markers: IMarkerSet;
    configuration: ICompareConfiguration;
    onDismiss?: () => void;
}

const CompareRadialNavOverlay: React.FC<ICompareRadialNavOverlayProps> = React.memo((props) => {
    const {
        model,
        isHidden,
        configuration,
        onDismiss,
    } = props;
    const [mountOnce, setMountOnce] = useState(false);
    const [instantToggle, setInstantToggle] = useState(false);
    const previousIsHiddenRef = useRef(isHidden);

    useEffect(() => {
        // Lazily mount heavy radial menu only when shown once.
        if (!isHidden && !mountOnce) {
            setMountOnce(true);
        }
    }, [isHidden, mountOnce]);

    useEffect(() => {
        // After first open->close cycle, switch to instant toggles.
        if (!instantToggle && previousIsHiddenRef.current === false && isHidden === true) {
            setInstantToggle(true);
        }

        previousIsHiddenRef.current = isHidden;
    }, [isHidden, instantToggle]);

    const onNavigate = async (links: string[]) => {
        onDismiss?.();
        await Delay(100);
        await ScrollToPositionCascaded(links);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss?.();
            }
        };

        if (onDismiss && !isHidden) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onDismiss, isHidden]);

    const menuItems: IRadialMenuItem[] = useMemo(() => {
        if (!mountOnce || !model) {
            return [];
        }

        const includeItem = (changed?: boolean) => !configuration.showDifferencesOnly || !!changed;

        const buildEntityItems = (panelId: string, list: IEntitiesContainerVM): IRadialMenuItem[] => {
            const entityItems: IRadialMenuItem[] = [];

            list.entities.forEach((entity, index) => {

                // if the entity itself is not changed and we are showing differences only, skip adding this entity to the menu
                if (!includeItem(entity.changed)) {
                    return;
                }

                const entityBaseId = `${panelId}-${entity.id}`;
                entityItems.push({
                    key: `${panelId}-entity-${entity.id}`,
                    label: entity.friendlyName ?? `Entity ${index + 1}`,
                    highlight: entity.changed,
                    getItems: async () => {
                        const items: IRadialMenuItem[] = [];

                        if (entity.propertiesVM.rowsVM.length > 0 && includeItem(entity.propertiesVM.changed)) {
                            items.push({
                                key: `${entityBaseId}-properties`,
                                label: 'Properties',
                                highlight: entity.propertiesVM.changed,
                                onClick: () => onNavigate([
                                    panelId,
                                    entityBaseId,
                                    `${entityBaseId}-properties`
                                ]),
                            });
                        }

                        if (entity.referencesVM && entity.referencesVM.rowsVM.length > 0 && includeItem(entity.referencesVM.changed)) {
                            items.push({
                                key: `${entityBaseId}-references`,
                                label: 'References',
                                highlight: entity.referencesVM.changed,
                                onClick: () => onNavigate([
                                    panelId,
                                    entityBaseId,
                                    `${entityBaseId}-references`
                                ]),
                            });
                        }

                        entity.tabsVM
                            .filter((tab) => includeItem(tab.changed))
                            .forEach((tab) => {

                                if (!includeItem(tab.changed)) {
                                    return;
                                }

                                const tabId = tab.tabDefinition.tab.bcbs_config_tabid!;
                                items.push({
                                    key: `${entityBaseId}-tab-${tabId}`,
                                    label: tab.tabDefinition.tab.bcbs_name ?? 'Tab',
                                    highlight: tab.changed,
                                    getItems: async () => {
                                        const sectionItems: IRadialMenuItem[] = [];
                                        tab.sectionsVM.forEach((section) => {

                                            if (!includeItem(section.changed)) {
                                                return;
                                            }

                                            const sectionId = section.sectionDefinition.section.bcbs_config_sectionid!;
                                            sectionItems.push({
                                                key: `${entityBaseId}-section-${sectionId}`,
                                                label: section.sectionDefinition.section.bcbs_name ?? 'Section',
                                                highlight: section.changed,
                                                onClick: () => onNavigate([
                                                    panelId,
                                                    entityBaseId,
                                                    `${entityBaseId}-${tabId}`,
                                                    `${entityBaseId}-${sectionId}`,
                                                ]),
                                            });
                                        });

                                        return sectionItems;
                                    },
                                    onClick: () => onNavigate([
                                        panelId,
                                        entityBaseId,
                                        `${entityBaseId}-${tabId}`
                                    ]),
                                });
                            });

                        return items;
                    },
                    onClick: () => onNavigate([panelId, entityBaseId]),
                });
            });

            return entityItems;
        };

        const items: IRadialMenuItem[] = [];

        if (includeItem(model.summary.changed)) {
            items.push({
                key: 'summary',
                label: 'Summary',
                highlight: model.summary.changed,
                onClick: () => onNavigate(['summary-panel']),
            });
        }

        const singleEntityPanel = (
            panelId: string,
            label: string,
            list: IEntitiesContainerVM,
        ) => {
            if (!list?.entities?.length || !includeItem(list.changed)) {
                return;
            }

            const entity = list.entities[0];
            const entityBaseId = `${panelId}`;

            items.push({
                key: panelId,
                label,
                highlight: list.changed,
                getItems: async () => {
                    const entityItems: IRadialMenuItem[] = [];

                    if (entity.propertiesVM.rowsVM.length > 0 && includeItem(entity.propertiesVM.changed)) {
                        entityItems.push({
                            key: `${entityBaseId}-properties`,
                            label: 'Properties',
                            highlight: entity.propertiesVM.changed,
                            onClick: () => onNavigate([panelId, `${entityBaseId}-properties`]),
                        });
                    }

                    entity.tabsVM
                        .filter((tab) => includeItem(tab.changed))
                        .forEach((tab) => {
                            const tabId = tab.tabDefinition.tab.bcbs_config_tabid!;
                            entityItems.push({
                                key: `${entityBaseId}-tab-${tabId}`,
                                label: tab.tabDefinition.tab.bcbs_name ?? 'Tab',
                                highlight: tab.changed,
                                getItems: async () => {
                                    const sectionItems: IRadialMenuItem[] = [];

                                    tab.sectionsVM.forEach((section) => {
                                        if (!includeItem(section.changed)) {
                                            return;
                                        }

                                        const sectionId = section.sectionDefinition.section.bcbs_config_sectionid!;
                                        sectionItems.push({
                                            key: `${entityBaseId}-section-${sectionId}`,
                                            label: section.sectionDefinition.section.bcbs_name ?? 'Section',
                                            highlight: section.changed,
                                            onClick: () => onNavigate([
                                                panelId,
                                                `${entityBaseId}-${tabId}`,
                                                `${entityBaseId}-${sectionId}`,
                                            ]),
                                        });
                                    });

                                    return sectionItems;
                                },
                                onClick: () => onNavigate([panelId, `${entityBaseId}-${tabId}`]),
                            });
                        });

                    return entityItems;
                },
                onClick: () => onNavigate([entityBaseId]),
            });
        };

        const multiEntityPanel = (
            list: IEntitiesContainerVM,
        ) => {
            if (!list?.entities?.length || !includeItem(list.changed)) {
                return;
            }

            items.push({
                key: list.context,
                label: list.title,
                highlight: list.changed,
                getItems: async () => buildEntityItems(list.context, list),
                onClick: () => onNavigate([list.context]),
            });
        };

        multiEntityPanel(model.groupConfiguration);
        multiEntityPanel(model.nonStandardTemplate);
        multiEntityPanel(model.plan);
        multiEntityPanel(model.benefit);
        multiEntityPanel(model.accountsReceivable);
        multiEntityPanel(model.groupNumber);
        multiEntityPanel(model.cesNumber);
        multiEntityPanel(model.department);
        multiEntityPanel(model.feesAndPremium);
        multiEntityPanel(model.feesSummary);
        multiEntityPanel(model.stopLossSummary);
        multiEntityPanel(model.stopLoss);

        return items;
    }, [mountOnce, model, onDismiss, configuration.showDifferencesOnly]);

    const backdropStyle = {
        ...(isHidden ? QUICK_NAV_FLOATING_BACKDROP_HIDDEN : QUICK_NAV_FLOATING_BACKDROP_VISIBLE),
        ...(instantToggle ? { transition: 'none' } : {}),
    };

    return (
        <ThemeProvider theme={DarkTheme}>
            <div
                style={backdropStyle}
                onClick={onDismiss}
            />
            {mountOnce && (
                <RadialMenu
                    items={menuItems}
                    isOpen={!isHidden}
                    onDismiss={onDismiss}
                    disableAnimations={(model?.groupNumber?.entities?.length ?? 0) > 100} // Disable animations if there are no group numbers to avoid the weird bounce effect of an empty menu
                />
            )}
        </ThemeProvider>
    );
});

export default CompareRadialNavOverlay;
